import { Fragment, useMemo } from 'react'
import { Focusable } from 'react-aria-components'

import Tooltip from '@components/designSystem/Tooltip'
import TransformableImage from '@components/productImage/TransformableImage'
import { DATA_FACING_KEY } from '@components/shelfDetails/Facing'
import ProductTooltip from '@components/shelfDetails/ProductTooltip'
import ShelfContainer from '@components/shelfDetails/ShelfContainer'
import ShelfSkeleton from '@components/shelfDetails/ShelfSkeleton'
import { useTooltipPortalContainer } from '@components/shelfDetails/TooltipPortalContext'
import {
  BOARD_HEIGHT_M,
  type LaidOutFacing,
  layoutLayer,
  productsByGtin,
  usableLayerHeight,
  withRoomAboveTopLayer
} from '@components/shelfDetails/shelfLikeLayout'
import { useShelfHoverState } from '@components/shelfDetails/utils'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { useProductsFromShelf } from '@state/product'
import { useShelf } from '@state/shelf/queries'
import { cx } from '@utils/cx.ts'

import type { FacingProducer } from './ShelfDetails'
import type { TransformedPriceLabel } from '@state/shelf/types'

interface ShelfProps {
  shelfId: string
  facingProducer?: FacingProducer
}

const pct = (fraction: number) => `${fraction * 100}%`

/** Width of a price label in meters; the height is 3 cm, centred in the 4 cm shelf edge. */
const LABEL_WIDTH_M = 0.04

/**
 * Product view that reads like a real shelf: one image per facing, cropped to the product outline,
 * on the measured shelf boards. See spec 1.2 of the shelf visualization epic.
 */
function ShelfLikeViewShelf({ shelfId, facingProducer }: ShelfProps) {
  const { data, isLoading } = useShelf(shelfId)
  const shelf = useMemo(() => data && withRoomAboveTopLayer(data), [data])
  const { products, isLoading: areProductsLoading } = useProductsFromShelf(shelfId)
  const { hoverState, onPointerMove, onPointerLeave } = useShelfHoverState()
  const lookup = useMemo(() => productsByGtin(products), [products])

  if (isLoading || !shelf) return <ShelfSkeleton />

  const boardHeight = BOARD_HEIGHT_M / shelf.height

  return (
    <ShelfContainer
      aspectRatio={shelf.width / shelf.height}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
    >
      {/* Same DOM order as the old product view: layers top to bottom, facings left to right */}
      {shelf.layers.slice().reverse().map((layer) => {
        const layerHeight = usableLayerHeight(layer, boardHeight)
        const layerAspect = shelf.width / (layerHeight * shelf.height)
        const facings = areProductsLoading ? [] : layoutLayer(layer.facings.slice().reverse(), lookup, layerAspect)

        return (
          <Fragment key={layer.id}>
            <div
              className='absolute left-0 z-10 w-full bg-primary shadow-md'
              style={{
                bottom: pct(Math.max(0, layer.y - boardHeight)),
                height: pct(Math.min(boardHeight, layer.y)),
              }}
            >
              {layer.priceLabels.map(label => (
                <PriceLabelTag key={label.id} label={label} width={LABEL_WIDTH_M / shelf.width} />
              ))}
            </div>
            <div
              data-testid={`shelf:layer:${layer.id}`}
              className='absolute w-full'
              style={{ bottom: pct(layer.y), height: pct(layerHeight) }}
            >
              {facings.map((laidOut) => {
                const { facing, product } = laidOut
                const state = !hoverState ? 'default' : hoverState === facing.id ? 'hovered' : 'dimmed'

                return (
                  <ShelfLikeFacing
                    key={facing.id}
                    laidOut={laidOut}
                    state={state}
                    className={facingProducer?.({ viewType: 'product', facing, product, state })}
                  />
                )
              })}
            </div>
          </Fragment>
        )
      })}
    </ShelfContainer>
  )
}

interface ShelfLikeFacingProps {
  laidOut: LaidOutFacing
  state: 'default' | 'dimmed' | 'hovered'
  className?: string
}

interface PriceLabelTagProps {
  label: TransformedPriceLabel
  /** Fraction of the shelf width */
  width: number
}

/**
 * A price label on the shelf edge, at its measured position. Not moved with overflowing facings on
 * purpose: if facings and labels don't line up, that should be visible.
 */
function PriceLabelTag({ label, width }: PriceLabelTagProps) {
  const { t } = useTypedTranslation()
  const portalContainer = useTooltipPortalContainer()

  if (label.left < -width / 2 || label.left > 1 + width / 2) return null

  return (
    <Tooltip
      title={label.gtin
        ? t('storeMap.shelf.priceLabel.gtin', { gtin: label.gtin })
        : t('storeMap.shelf.priceLabel.noGtin')}
      placement='bottom'
      portalContainer={portalContainer}
    >
      <Focusable>
        <div
          data-testid={`shelf:price-label:${label.id}`}
          className={cx(
            'absolute bottom-[12.5%] h-3/4 rounded-[1px] border bg-primary',
            label.gtin ? 'border-utility-gray-500' : 'border-dashed border-utility-gray-600'
          )}
          style={{ left: pct(label.left - width / 2), width: pct(width) }}
        />
      </Focusable>
    </Tooltip>
  )
}

function ShelfLikeFacing({ laidOut, state, className }: ShelfLikeFacingProps) {
  const { t } = useTypedTranslation()
  const { facing, product, kind, isOutOfShelf } = laidOut

  const hint = [
    product && product.name !== facing.gtin && `GTIN ${facing.gtin}`,
    kind === 'noImage' && t('storeMap.shelf.facingState.noImage'),
    isOutOfShelf && t('common.outOfStock'),
  ].filter(Boolean).join(' · ')

  return (
    <ProductTooltip product={product} gtin={facing.gtin} hint={hint || undefined}>
      <div
        role='gridcell'
        className={cx(
          'absolute bottom-0 transition-opacity duration-300',
          state === 'dimmed' && 'opacity-40 duration-0',
          className
        )}
        style={{ left: pct(laidOut.left), width: pct(laidOut.width), height: pct(laidOut.boxHeight) }}
        data-testid={`shelf:facing:${facing.id}`}
        {...{ [DATA_FACING_KEY]: facing.id }}
      >
        <div
          className='absolute bottom-0'
          style={{
            left: pct((1 - laidOut.contentWidth / laidOut.width) / 2),
            width: pct(laidOut.contentWidth / laidOut.width),
            height: pct(laidOut.contentHeight / laidOut.boxHeight),
          }}
        >
          <FacingContent laidOut={laidOut} />
        </div>
      </div>
    </ProductTooltip>
  )
}

function FacingContent({ laidOut }: { laidOut: LaidOutFacing }) {
  const { kind, product, outline, isOutOfShelf } = laidOut

  if (kind === 'image' && product?.imageUrl && outline) {
    // Crop to the outline: scale the image up so the outline fills the wrapper, then shift it
    const width = outline.x1 - outline.x0
    const height = outline.y1 - outline.y0
    return (
      <div className='relative size-full overflow-hidden'>
        <TransformableImage
          type='shelfProductImage'
          src={product.imageUrl}
          alt=''
          className='absolute max-w-none animate-fade-in object-contain object-bottom'
          style={{
            width: pct(1 / width),
            height: pct(1 / height),
            left: pct(-outline.x0 / width),
            top: pct(-outline.y0 / height),
            mixBlendMode: 'multiply',
            opacity: isOutOfShelf ? 0.35 : undefined,
          }}
        />
      </div>
    )
  }

  if (kind === 'noGtin') {
    return (
      <div className={`
        flex size-full items-center justify-center overflow-hidden rounded-xs border-[1.5px] border-dashed
        border-utility-gray-600 text-xs font-bold text-utility-gray-600
      `}>
        ?
      </div>
    )
  }

  return (
    <div
      className='flex size-full items-end justify-center overflow-hidden rounded-t-sm rounded-b-xs bg-utility-gray-400'
      style={{ opacity: isOutOfShelf ? 0.45 : undefined }}
    >
      <BarcodeGlyph className='mb-[8%] w-3/5 max-w-5.5 text-utility-gray-50' />
    </div>
  )
}

const BARCODE_BARS = [0, 2, 3, 5, 8, 9, 11, 13, 14, 17, 19, 20, 22, 25, 26, 28]

export function BarcodeGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox='0 0 30 16' aria-hidden='true' className={className}>
      {BARCODE_BARS.map((x, i) => (
        <rect key={x} x={x} y={0} width={i % 3 ? 1 : 1.6} height={16} fill='currentColor' />
      ))}
    </svg>
  )
}

export default ShelfLikeViewShelf
